import { useEffect, useSyncExternalStore } from "react";
import type { NavItem } from "./use-nav-items";

const STORAGE_KEY = "volca-seen-new-nav";

const listeners = new Set<() => void>();

const readRaw = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) ?? "[]";
  } catch {
    return "[]";
  }
};

const parse = (raw: string): string[] => {
  try {
    const value: unknown = JSON.parse(raw);
    return Array.isArray(value)
      ? value.filter((href) => typeof href === "string")
      : [];
  } catch {
    return [];
  }
};

const markSeen = (hrefs: string[]) => {
  const seen = parse(readRaw());
  const next = [...new Set([...seen, ...hrefs])];
  if (next.length === seen.length) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Private mode can refuse storage. The badge shows again next visit.
  }
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useUnseenNewItems = (items: NavItem[]) => {
  const seen = parse(useSyncExternalStore(subscribe, readRaw, () => "[]"));
  const opened = items
    .filter((item) => item.isNew && item.isActive)
    .map((item) => item.href)
    .join(" ");

  useEffect(() => {
    if (opened) markSeen(opened.split(" "));
  }, [opened]);

  return new Set(
    items
      .filter(
        (item) => item.isNew && !item.isActive && !seen.includes(item.href),
      )
      .map((item) => item.href),
  );
};
